if (!customElements.get('dynamic-dates')) {
  // Full English weekday names, used only to match the merchant's "Week days to skip" setting.
  // Independent from the (customizable) day_labels used for display, matching Shrine's own
  // behavior: skip-matching is always in English regardless of display label language.
  const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
  const DEFAULT_DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const DEFAULT_MONTH_LABELS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  function ordinal(n) {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return `${n}st`;
    if (j === 2 && k !== 12) return `${n}nd`;
    if (j === 3 && k !== 13) return `${n}rd`;
    return `${n}th`;
  }

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function isSkipped(date, skipWeekdays, skipDates) {
    const weekdayName = WEEKDAY_NAMES[date.getDay()];
    if (skipWeekdays.has(weekdayName)) return true;

    const mmdd = `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
    return skipDates.has(mmdd);
  }

  // Walks forward from `startDate`, counting only days that are not skipped, until `days` of
  // them have passed. `days` itself is never skip-checked (0 always resolves to startDate
  // unchanged - e.g. checkpoint #1's default "0 required days" represents "today").
  function addRequiredDays(startDate, days, skipWeekdays, skipDates) {
    const result = new Date(startDate);
    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      if (!isSkipped(result, skipWeekdays, skipDates)) {
        remaining -= 1;
      }
    }
    return result;
  }

  function formatDate(date, format, dayLabels, monthLabels) {
    // dayLabels is Monday-first (matches the setting's default "Mon, Tue, Wed, ..."),
    // while Date#getDay() is Sunday-first - convert.
    const mondayFirstIndex = (date.getDay() + 6) % 7;
    const dayLabel = dayLabels[mondayFirstIndex] || '';
    const monthLabel = monthLabels[date.getMonth()] || '';
    const day = date.getDate();
    const dd = pad2(day);
    const mm = pad2(date.getMonth() + 1);

    switch (format) {
      case 'day_mm_dd':
        return `${dayLabel}, ${monthLabel} ${ordinal(day)}`;
      case 'day_dd_mm':
        return `${dayLabel}, ${day}. ${monthLabel}`;
      case 'dd_mm':
        return `${day}. ${monthLabel}`;
      case 'dd_mm_no_dot':
        return `${day} ${monthLabel}`;
      case 'day_dd_mm_numeric':
        return `${dayLabel}, ${dd}. ${mm}.`;
      case 'dd_mm_numeric':
        return `${dd}. ${mm}.`;
      case 'mm_dd':
      default:
        return `${monthLabel} ${ordinal(day)}`;
    }
  }

  function parseList(value) {
    return (value || '')
      .split(',')
      .map((item) => item.trim())
      .filter(Boolean);
  }

  customElements.define(
    'dynamic-dates',
    class DynamicDates extends HTMLElement {
      connectedCallback() {
        const format = this.dataset.dateFormat || 'mm_dd';
        const parsedDayLabels = parseList(this.dataset.dayLabels);
        const parsedMonthLabels = parseList(this.dataset.monthLabels);
        const dayLabels = parsedDayLabels.length ? parsedDayLabels : DEFAULT_DAY_LABELS;
        const monthLabels = parsedMonthLabels.length ? parsedMonthLabels : DEFAULT_MONTH_LABELS;
        const skipWeekdays = new Set(parseList(this.dataset.daysToSkip).map((day) => day.toLowerCase()));
        const skipDates = new Set(parseList(this.dataset.datesToSkip));

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        this.querySelectorAll('[data-dynamic-date]').forEach((el) => {
          const text = el.dataset.text || '';
          const hasStart = text.indexOf('[start_date]') !== -1;
          const hasEnd = text.indexOf('[end_date]') !== -1;
          if (!hasStart && !hasEnd) return;

          const minDays = parseInt(el.dataset.minDays, 10) || 0;
          const maxDays = parseInt(el.dataset.maxDays, 10) || 0;

          let output = text;
          if (hasStart) {
            const startDate = addRequiredDays(today, minDays, skipWeekdays, skipDates);
            output = output.replace('[start_date]', formatDate(startDate, format, dayLabels, monthLabels));
          }
          if (hasEnd) {
            const endDate = addRequiredDays(today, maxDays, skipWeekdays, skipDates);
            output = output.replace('[end_date]', formatDate(endDate, format, dayLabels, monthLabels));
          }
          el.innerHTML = output;
        });
      }
    }
  );
}
